import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { userManagementAPI, authAPI } from '../services/api';

// Wellsync-inspired color palette - mature corporate healthcare aesthetic
const COLORS = {
  primary: '#1e40af',      // Deep corporate blue
  secondary: '#3b82f6',    // Bright blue
  accent: '#0891b2',       // Teal accent
  success: '#059669',      // Emerald green (deeper)
  warning: '#d97706',      // Amber (deeper)
  danger: '#dc2626',       // Red
  muted: '#64748b',        // Slate gray
  light: '#f8fafc',        // Very light slate
  white: '#ffffff',
  border: '#e2e8f0',       // Slate border
  dark: '#334155',         // Slate dark for text
};

const UserManagementPage = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    search: ''
  });
  const [loading, setLoading] = useState(true);
  
  // Hospital admins can only view users, not edit/create/delete
  const isViewOnly = user?.role === 'hospital_admin';

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: '',
    hospital_id: '',
    hospital_ids: []
  });

  const isRegionalRole = (role) => (role || '').trim() === 'Regional';

  const toggleHospital = (hospitalId) => {
    setFormData((prev) => {
      const exists = prev.hospital_ids.includes(hospitalId);
      return {
        ...prev,
        hospital_ids: exists
          ? prev.hospital_ids.filter((id) => id !== hospitalId)
          : [...prev.hospital_ids, hospitalId]
      };
    });
  };

  const selectAllHospitals = () => {
    setFormData((prev) => ({ ...prev, hospital_ids: hospitals.map((h) => h.Hospital_Id) }));
  };

  const clearAllHospitals = () => {
    setFormData((prev) => ({ ...prev, hospital_ids: [] }));
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersResponse, rolesResponse, hospitalsResponse] = await Promise.all([
        userManagementAPI.getUsers(filters),
        userManagementAPI.getRoles(),
        authAPI.getAllowedHospitals()
      ]);
      
      // Handle users response
      if (usersResponse.data && usersResponse.data.users) {
        setUsers(usersResponse.data.users);
      } else if (Array.isArray(usersResponse.data)) {
        setUsers(usersResponse.data);
      } else {
        setUsers([]);
      }
      
      // Handle roles response
      if (rolesResponse.data && rolesResponse.data.roles) {
        setRoles(rolesResponse.data.roles);
      } else if (Array.isArray(rolesResponse.data)) {
        setRoles(rolesResponse.data);
      } else {
        setRoles([]);
      }
      
      // Handle hospitals response (auth API returns { id, name }, older APIs return { Hospital_Id, Hospital_Name })
      const rawHospitals = hospitalsResponse.data && hospitalsResponse.data.hospitals
        ? hospitalsResponse.data.hospitals
        : (Array.isArray(hospitalsResponse.data) ? hospitalsResponse.data : []);
      setHospitals(rawHospitals.map(h => ({
        Hospital_Id: h.Hospital_Id ?? h.id,
        Hospital_Name: h.Hospital_Name ?? h.name
      })));
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const isRegional = isRegionalRole(formData.role);

      // Ensure hospital_id is properly converted: empty string or null becomes null, otherwise parse as int
      let hospitalId = null;
      if (formData.hospital_id && formData.hospital_id !== '' && formData.hospital_id !== 'null') {
        hospitalId = parseInt(formData.hospital_id, 10);
        if (isNaN(hospitalId)) {
          hospitalId = null;
        }
      }

      const payload = {
        username: formData.username,
        password: formData.password,
        role: formData.role,
        hospital_id: isRegional ? null : hospitalId,
        hospital_ids: isRegional ? formData.hospital_ids.map((id) => parseInt(id, 10)) : undefined
      };

      console.log('Creating user - formData:', formData);
      console.log('Creating user - payload:', payload);

      await userManagementAPI.createUser(payload);
      alert('User created successfully');
      setShowCreateModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Create user error:', error);
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateUser = async () => {
    try {
      const isRegional = isRegionalRole(formData.role);
      const payload = {
        username: formData.username || undefined,
        password: formData.password || undefined,
        role: formData.role || undefined,
        hospital_id: isRegional ? null : (formData.hospital_id ? parseInt(formData.hospital_id) : null),
        hospital_ids: isRegional ? formData.hospital_ids.map((id) => parseInt(id, 10)) : undefined
      };
      await userManagementAPI.updateUser(selectedUser.Emp_Id, payload);
      alert('User updated successfully');
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      loadData();
    } catch (error) {
      alert('Failed to update user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await userManagementAPI.deleteUser(userId);
      alert('User deleted successfully');
      loadData();
    } catch (error) {
      alert('Failed to delete user: ' + error.message);
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.Username || '',
      password: '',
      role: user.Role || '',
      hospital_id: user.Hospital_Id ? String(user.Hospital_Id) : '',
      hospital_ids: Array.isArray(user.Hospital_Ids) && user.Hospital_Ids.length
        ? user.Hospital_Ids
        : (user.Hospital_Id ? [user.Hospital_Id] : [])
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: '',
      hospital_id: '',
      hospital_ids: []
    });
  };

  const getRoleBadgeStyle = (role) => {
    switch (role) {
      case 'Superadmin':
        return { backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' };
      case 'Group':
        return { backgroundColor: '#f3e8ff', color: '#9333ea', border: '1px solid #e9d5ff' };
      case 'Regional ':
        return { backgroundColor: '#dbeafe', color: '#2563eb', border: '1px solid #bfdbfe' };
      case 'Ipp':
        return { backgroundColor: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' };
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'Superadmin': return 'Super Admin';
      case 'Group': return 'Group IPP';
      case 'Regional ': return 'Regional  Admin';
      case 'Ipp': return 'Hospital Admin';
      default: return role;
    }
  };

  const filteredUsers = users.filter(user => {
    if (filters.role && user.Role !== filters.role) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (user.Username || '').toLowerCase().includes(search);
    }
    return true;
  });

  const handleDownloadPdf = () => {
    const exportUsers = users.filter(
      u => !(u.Username || '').toLowerCase().trim().endsWith('@gonxt.tech')
    );
    if (exportUsers.length === 0) {
      alert('No users to download');
      return;
    }

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    const columns = [
      { label: 'Username', width: 72 },
      { label: 'Role', width: 42 },
      { label: 'Hospital', width: 68 },
    ];
    const tableWidth = columns.reduce((sum, col) => sum + col.width, 0);
    let y = margin + 4;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 64, 175);
    doc.text('EVE6 Portal — Users', margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated ${new Date().toLocaleString()}  •  ${exportUsers.length} users`, margin, y);
    y += 8;

    const drawTableHeader = () => {
      doc.setFillColor(248, 250, 252);
      doc.rect(margin, y, tableWidth, 8, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      let x = margin;
      columns.forEach(col => {
        doc.text(col.label, x + 2, y + 5.5);
        x += col.width;
      });
      y += 8;
      doc.setFont('helvetica', 'normal');
    };

    drawTableHeader();
    doc.setFontSize(9);

    exportUsers.forEach(u => {
      const cells = [
        String(u.Username || ''),
        getRoleDisplayName(u.Role) || '',
        u.Hospital_Name || (u.Hospital_Id ? '—' : 'All Hospitals'),
      ];
      const wrapped = cells.map((text, i) => doc.splitTextToSize(text, columns[i].width - 4));
      const rowHeight = Math.max(...wrapped.map(lines => lines.length)) * 4 + 3.5;

      if (y + rowHeight > pageHeight - margin) {
        doc.addPage();
        y = margin;
        drawTableHeader();
        doc.setFontSize(9);
      }

      doc.setTextColor(51, 65, 85);
      let x = margin;
      wrapped.forEach((lines, i) => {
        doc.text(lines, x + 2, y + 4.5);
        x += columns[i].width;
      });
      y += rowHeight;
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, margin + tableWidth, y);
    });

    doc.save(`evesix-users-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #f8fafc, #f1f5f9)', padding: '32px' }}>
      {/* Wellsync-style Header */}
      <div style={{ marginBottom: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '600', color: COLORS.dark, marginBottom: '8px' }}>
            User Management
          </h1>
          <p style={{ color: COLORS.muted, fontSize: '14px' }}>
            Manage system users, roles, and hospital assignments
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleDownloadPdf}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: COLORS.white,
              color: COLORS.primary,
              fontWeight: '600',
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
            }}
          >
            ⬇ Download PDF
          </button>
          {!isViewOnly && (
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              style={{
                padding: '12px 24px',
                backgroundColor: '#2563eb',
                color: COLORS.white,
                fontWeight: '600',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)'
              }}
            >
              + Create User
            </button>
          )}
          {isViewOnly && (
            <span style={{ padding: '12px 24px', backgroundColor: COLORS.light, color: COLORS.muted, borderRadius: '12px', fontSize: '14px', border: '1px solid ' + COLORS.border }}>
              View Only Mode
            </span>
          )}
        </div>
      </div>

      {/* Wellsync-style Filters */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Search
            </label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search by username..."
              style={{
                width: '100%',
                backgroundColor: COLORS.light,
                color: COLORS.primary,
                borderRadius: '8px',
                padding: '10px 16px',
                border: `1px solid ${COLORS.border}`,
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Role
            </label>
            <select
              value={filters.role}
              onChange={(e) => setFilters({ ...filters, role: e.target.value })}
              style={{
                width: '100%',
                backgroundColor: COLORS.light,
                color: COLORS.primary,
                borderRadius: '8px',
                padding: '10px 16px',
                border: `1px solid ${COLORS.border}`,
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.role_name} value={role.role_name}>
                  {role.display_name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={loadData}
              style={{
                width: '100%',
                padding: '10px 16px',
                backgroundColor: COLORS.light,
                color: COLORS.primary,
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Wellsync-style Users Table */}
      <div style={{
        backgroundColor: COLORS.white,
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${COLORS.border}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: COLORS.light }}>
                <th style={{ textAlign: 'left', padding: '16px 24px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>User</th>
                <th style={{ textAlign: 'left', padding: '16px 24px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Role</th>
                <th style={{ textAlign: 'left', padding: '16px 24px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Hospital</th>
                <th style={{ textAlign: 'left', padding: '16px 24px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Active</th>
                <th style={{ textAlign: 'left', padding: '16px 24px', color: COLORS.muted, fontWeight: '500', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" style={{ padding: '48px', textAlign: 'center', color: COLORS.muted }}>
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '48px', textAlign: 'center', color: COLORS.muted }}>
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.Emp_Id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '16px 24px' }}>
                      <div>
                        <p style={{ color: COLORS.primary, fontWeight: '500', fontSize: '14px', margin: 0 }}>
                          {user.Username}
                        </p>
                        <p style={{ color: COLORS.muted, fontSize: '12px', margin: '4px 0 0 0' }}>
                          ID: {user.Emp_Id}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        ...getRoleBadgeStyle(user.Role)
                      }}>
                        {getRoleDisplayName(user.Role)}
                      </span>
                    </td>
                    <td style={{ padding: '16px 24px', color: COLORS.primary, fontSize: '14px' }}>
                      {user.Hospital_Name || (user.Hospital_Id ? `Hospital ${user.Hospital_Id}` : 'All Hospitals')}
                    </td>
                    <td style={{ padding: '16px 24px', color: COLORS.muted, fontSize: '14px' }}>
                      {user.Last_Login ? new Date(user.Last_Login).toLocaleString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      }) : 'Never'}
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      {isViewOnly ? (
                        <span style={{ color: COLORS.muted, fontSize: '14px' }}>-</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <button
                            onClick={() => handleEditClick(user)}
                            style={{
                              color: COLORS.accent,
                              fontSize: '14px',
                              fontWeight: '500',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.Emp_Id)}
                            style={{
                              color: COLORS.danger,
                              fontSize: '14px',
                              fontWeight: '500',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: COLORS.primary, marginBottom: '24px' }}>
              Create New User
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Email / Username *
                </label>
                <input
                  type="email"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="user@example.com"
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Role *
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.role_name} value={role.role_name}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                  Hospital Admin: Single hospital | Regional : Multiple hospitals | Group IPP: All hospitals
                </p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Hospital Assignment
                </label>
                {isRegionalRole(formData.role) ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '8px' }}>
                      <button type="button" onClick={selectAllHospitals} style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                        Select All
                      </button>
                      <button type="button" onClick={clearAllHospitals} style={{ background: 'none', border: 'none', color: COLORS.muted, fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                        Clear All
                      </button>
                    </div>
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: COLORS.light,
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.border}`,
                      padding: '8px'
                    }}>
                      {hospitals.map((hospital) => (
                        <label key={hospital.Hospital_Id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '14px', color: COLORS.primary, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.hospital_ids.includes(hospital.Hospital_Id)}
                            onChange={() => toggleHospital(hospital.Hospital_Id)}
                          />
                          {hospital.Hospital_Name}
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                      {formData.hospital_ids.length} hospital(s) selected
                    </p>
                  </>
                ) : (
                  <>
                    <select
                      value={formData.hospital_id}
                      onChange={(e) => setFormData({ ...formData, hospital_id: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: COLORS.light,
                        color: COLORS.primary,
                        borderRadius: '8px',
                        padding: '12px 16px',
                        border: `1px solid ${COLORS.border}`,
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="">All Hospitals (Group level)</option>
                      {hospitals.map((hospital) => (
                        <option key={hospital.Hospital_Id} value={hospital.Hospital_Id}>
                          {hospital.Hospital_Name}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                      Leave empty for Group IPP users
                    </p>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: COLORS.light,
                  color: COLORS.primary,
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                style={{
                  padding: '12px 24px',
                  backgroundColor: COLORS.accent,
                  color: COLORS.white,
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: COLORS.white,
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: COLORS.primary, marginBottom: '24px' }}>
              Edit User
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Email / Username
                </label>
                <input
                  type="email"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  New Password (leave empty to keep current)
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter new password"
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Role
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  style={{
                    width: '100%',
                    backgroundColor: COLORS.light,
                    color: COLORS.primary,
                    borderRadius: '8px',
                    padding: '12px 16px',
                    border: `1px solid ${COLORS.border}`,
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                >
                  {roles.map((role) => (
                    <option key={role.role_name} value={role.role_name}>
                      {role.display_name}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                  Hospital Admin: Single hospital | Regional : Multiple hospitals | Group IPP: All hospitals
                </p>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: COLORS.muted, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Hospital Assignment
                </label>
                {isRegionalRole(formData.role) ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '8px' }}>
                      <button type="button" onClick={selectAllHospitals} style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                        Select All
                      </button>
                      <button type="button" onClick={clearAllHospitals} style={{ background: 'none', border: 'none', color: COLORS.muted, fontSize: '12px', fontWeight: '500', cursor: 'pointer' }}>
                        Clear All
                      </button>
                    </div>
                    <div style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: COLORS.light,
                      borderRadius: '8px',
                      border: `1px solid ${COLORS.border}`,
                      padding: '8px'
                    }}>
                      {hospitals.map((hospital) => (
                        <label key={hospital.Hospital_Id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', fontSize: '14px', color: COLORS.primary, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={formData.hospital_ids.includes(hospital.Hospital_Id)}
                            onChange={() => toggleHospital(hospital.Hospital_Id)}
                          />
                          {hospital.Hospital_Name}
                        </label>
                      ))}
                    </div>
                    <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                      {formData.hospital_ids.length} hospital(s) selected
                    </p>
                  </>
                ) : (
                  <>
                    <select
                      value={formData.hospital_id}
                      onChange={(e) => setFormData({ ...formData, hospital_id: e.target.value })}
                      style={{
                        width: '100%',
                        backgroundColor: COLORS.light,
                        color: COLORS.primary,
                        borderRadius: '8px',
                        padding: '12px 16px',
                        border: `1px solid ${COLORS.border}`,
                        fontSize: '14px',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="">All Hospitals (Group level)</option>
                      {hospitals.map((hospital) => (
                        <option key={hospital.Hospital_Id} value={hospital.Hospital_Id}>
                          {hospital.Hospital_Name}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: '12px', color: COLORS.muted, marginTop: '4px' }}>
                      Leave empty for Group IPP users
                    </p>
                  </>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                  resetForm();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: COLORS.light,
                  color: COLORS.primary,
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.border}`,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateUser}
                style={{
                  padding: '12px 24px',
                  backgroundColor: COLORS.accent,
                  color: COLORS.white,
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
